import { useCallback, useEffect, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router";

import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import Hidden from "@material-ui/core/Hidden";
import Skeleton from '@material-ui/lab/Skeleton';
import { DateTime } from "luxon";

import { ReservationService } from "../../services";
import { ReservationResponse } from "../../services/models";
import {
  formatError,
  makeFloor,
  StatusId,
  handleSectionIcon,
  makeLuxonDate,
  snackbarUtils,
  mockTransport
} from "../../utils";

import Head from '../../components/Head';
import Layout from "../../layout";
import CheckOutDialog from "../../components/Schedules/CheckOutDialog";
import CancelScheduleDialog from './CancelScheduleDialog';
import useStyles from './styles'
import useCommonStyles from '../../styles/common';
import FormDialog from "../../components/Schedules/FormDialog";

type TypeScheduleData = {
  title: string,
  fields: {
    [key: string]: string
  };
};
interface ScheduleData {
  building: TypeScheduleData;
  transport: TypeScheduleData;
  date: TypeScheduleData;
}

const handleScheduleData = (schedule: ReservationResponse): ScheduleData => {
  return (
    {
      "building": {
        title: "Seu Ambiente",
        fields: {
          'Prédio': schedule.buildingTitle,
          'Piso': `${makeFloor(schedule.floor.toString())}`,
          'Setor': schedule.sectionName,
          'Mesa': schedule.desk,
        },
      },
      "transport": {
        title: "Meio de Transporte",
        fields: {
          'Transporte': mockTransport(schedule.transportType),
          // 'Garagem': schedule.parkingLocal,
          'Vaga': schedule.parkingLocal,
        },
      },
      "date": {
        title: "Período de Agendamento",
        fields: {
          'Data': makeLuxonDate(schedule.date),
          'Check-in': makeLuxonDate(schedule.checkInDate, DateTime.TIME_24_WITH_SECONDS),
          'Check-out': makeLuxonDate(schedule.checkOutDate, DateTime.TIME_24_WITH_SECONDS),
        },
      },
    }
  )
}

const StyledSkeleton = () => (
  <Skeleton variant="rect" width="100%" height={68} />
)

type MatchParams = {
  id: string;
}
interface Props extends RouteComponentProps<MatchParams> { }

const MySchedule = (props: Props) => {
  const commonClasses = useCommonStyles();
  const { push } = useHistory();

  const [cancelDialogIsOpen, setCancelDialogIsOpen] = useState(false);
  const [checkOutDialogIsOpen, setCheckOutDialogIsOpen] = useState(false);
  const [formDialogIsOpen, setFormDialogIsOpen] = useState(false);

  const [schedule, setSchedule] = useState<ReservationResponse>({} as ReservationResponse);
  const [formattedSchedule, setFormattedSchedule] = useState<ScheduleData | null>(null);
  const classes = useStyles({ image: handleSectionIcon(schedule.sectionName) });

  const shouldShowCancel = () => schedule.statusId === StatusId.checkIn;
  const reservationId = Number(props.match.params.id);

  const handleCancelDialog = useCallback(() => setCancelDialogIsOpen(value => !value), []);
  const handleCheckOutDialog = useCallback(() => setCheckOutDialogIsOpen(value => !value), []);
  const handleFormDialog = useCallback(() => setFormDialogIsOpen(value => !value), []);

  useEffect(() => {
    const fetchReservation = async () => {
      try {
        const { data } = await ReservationService.show(reservationId);
        setSchedule(data);
        setFormattedSchedule(handleScheduleData(data));
      } catch (error) {
        console.log('Dados Agendamento: ', error.message);
        snackbarUtils.error(formatError(error))
      }
    };
    fetchReservation();
  }, [reservationId, formDialogIsOpen, cancelDialogIsOpen]);

  const handleTitleStatusId = useCallback((status_id: number) => {
    switch (status_id) {
      case StatusId.cancelled:
        return <Typography id="cancelled-title" variant="h2">Agendamento Cancelado</Typography>;
      case StatusId.done:
        return <Typography variant="h2">Agendamento Finalizado</Typography>;
      default:
        return <Typography variant="h2">Meu Agendamento</Typography>;
    }
  }, []);

  const handleButtonStatusId = useCallback((schedule: ReservationResponse) => {
    const handleCheckIn = () => push(`/check-in/${schedule.id}`);
    const isToday = () => DateTime.now().hasSame(DateTime.fromISO(schedule.date), "day");

    switch (schedule.statusId) {
      case StatusId.checkIn:
        return (
          isToday() ?
            <Button
              data-cy="check-in-button"
              variant='outlined'
              onClick={handleCheckIn}
              className={commonClasses.yellowButton}
            >
              Check In
            </Button>
            :
            null
        )
      case StatusId.checkOut:
        return (
          <Button
            data-cy="check-out-dialog-button"
            variant='outlined'
            onClick={handleCheckOutDialog}
            className={commonClasses.yellowButton}
          >
            Check Out
          </Button>
        )
      case StatusId.form:
        return (
          <Button
            data-cy="form-dialog-button"
            onClick={handleFormDialog}
            className={classes.formButton}
          >
            Como foi seu dia de trabalho?
          </Button>
        )
      case StatusId.cancelled:
        return (
          <Typography variant="h4" className={classes.cancelled}>Cancelado</Typography>
        )
      default:
        return null
    }
  }, [
    classes.cancelled,
    classes.formButton,
    commonClasses.yellowButton,
    handleCheckOutDialog,
    handleFormDialog,
    push
  ]);

  return (
    <Layout
      page="mySchedules"
      backLink="/agendamentos"
      innerPage>
      <Head>Meu agendamento</Head>
      <Box component="section" className={classes.container}>
        <Box>
          <Hidden only="xs">
            {handleTitleStatusId(schedule.statusId)}
          </Hidden>

          <Box component="section" className={classes.contentWrapper}>
            <Box className={classes.contentHeader} style={{ backgroundColor: schedule.color }}>
              <Grid container item xs={12} spacing={2}>
                <Hidden smDown>
                  {!!formattedSchedule?.building ? (
                    Object.entries(formattedSchedule.building.fields).map(([key, value]) => (
                      <Grid key={key} item xs>
                        <Typography variant="h3">{key}</Typography>
                        <Typography>{value}</Typography>
                      </Grid>
                    ))
                  ) : (
                    <StyledSkeleton />
                  )}
                </Hidden>
                <Hidden smUp>
                  <Grid item xs={6}>
                    {schedule.statusId === StatusId.done &&
                      <Typography id="done-title" variant="h2" >
                        Agendamento Finalizado
                      </Typography>
                    }
                  </Grid>
                </Hidden>
                <Grid container item xs={12} sm={3} justify="flex-end">
                  {handleButtonStatusId(schedule)}
                </Grid>
              </Grid>
            </Box>

            <Grid container className={classes.dataContainer}>
              <Hidden mdUp>
                <Grid item xs={12} md={6}>
                  <Typography variant="h3">{formattedSchedule?.building.title}</Typography>
                  <Grid container item xs={12} md={10} lg={8}>
                    {!!formattedSchedule?.building ? (
                      Object.entries(formattedSchedule.building.fields).map(([key, value]) => (
                        <Grid key={key} item xs>
                          <Typography variant="h4">{key}</Typography>
                          <Typography>{value}</Typography>
                        </Grid>
                      ))
                    ) : (
                      <StyledSkeleton />
                    )}
                  </Grid>
                </Grid>
              </Hidden>

              <Hidden mdUp>
                <Divider variant="middle" component="div" style={{ width: '100%' }} />
              </Hidden>

              <Grid item xs={12} md>
                <Typography variant="h3">{formattedSchedule?.transport.title}</Typography>
                <Grid container item xs={12} md={10} lg={8}>
                  {!!formattedSchedule?.transport ? (
                    Object.entries(formattedSchedule.transport.fields).map(([key, value]) => (
                      !!value &&
                      <Grid key={key} item xs={4} md>
                        <Typography variant="h4">{key}</Typography>
                        <Typography>{value}</Typography>
                      </Grid>
                    ))
                  ) : (
                    <StyledSkeleton />
                  )}
                </Grid>
              </Grid>

              <Hidden mdUp>
                <Divider variant="middle" component="div" style={{ width: '100%' }} />
              </Hidden>
              <Grid item xs={12} md>
                <Typography variant="h3">{formattedSchedule?.date.title}</Typography>
                <Grid container item xs={12} md={11} lg={8}>
                  {!!formattedSchedule?.date ? (
                    Object.entries(formattedSchedule.date.fields).map(([key, value]) => (
                      <Grid key={key} item xs>
                        <Typography variant="h4">{key}</Typography>
                        <Typography>{value}</Typography>
                      </Grid>
                    ))
                  ) : (
                    <StyledSkeleton />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {shouldShowCancel() &&
          <Grid container className={classes.buttonContainer}>
            <Button
              data-cy="cancel-dialog-button"
              onClick={handleCancelDialog}
              className={commonClasses.outlinedButton}
            >
              Cancelar Agendamento
            </Button>
          </Grid>
        }
      </Box>

      <CancelScheduleDialog reservationId={schedule.id} isOpen={cancelDialogIsOpen} handleClose={handleCancelDialog} />
      <CheckOutDialog
        isOpen={checkOutDialogIsOpen}
        handleClose={handleCheckOutDialog}
        reservationId={schedule.id}
        handleFormDialog={handleFormDialog}
      />
      <FormDialog isOpen={formDialogIsOpen} handleClose={handleFormDialog} reservationId={schedule.id} />
    </Layout>
  );
}

export default MySchedule;